import { Directive, Input, OnInit, OnDestroy, HostListener, inject, signal, computed, DestroyRef } from '@angular/core';
import { ToastService } from './toast.service';

@Directive({
    selector: '[appToastTimer]',
    standalone: true,
    exportAs: 'toastTimer',
})
export class ToastDirective implements OnInit, OnDestroy {
    @Input() toast!: { id: number; duration?: number };

    private readonly toastService = inject(ToastService);
    private readonly destroyRef = inject(DestroyRef);

    // Core state signals
    private readonly duration = signal(0);
    private readonly startTime = signal(0);
    private readonly totalPausedTime = signal(0);
    private readonly isPaused = signal(false);
    private readonly pauseStartTime = signal(0);
    private readonly currentTime = signal(Date.now());

    // Computed progress
    readonly progress = computed(() => {
        const dur = this.duration();
        if (dur <= 0) return 1;

        const start = this.startTime();
        const current = this.currentTime();
        const totalPaused = this.totalPausedTime();

        // Calculate elapsed time excluding paused periods
        const elapsed = current - start - totalPaused;
        const remaining = Math.max(dur - elapsed, 0);

        return remaining / dur;
    });

    // Computed remaining time
    private readonly remainingTime = computed(() => {
        const prog = this.progress();
        const dur = this.duration();
        return dur > 0 ? prog * dur : 0;
    });

    private timeoutId: ReturnType<typeof setTimeout> | null = null;
    private animationFrameId: number | null = null;

    constructor() {
        // Cleanup on destroy
        this.destroyRef.onDestroy(() => this.cleanup());
    }

    ngOnInit() {
        if (this.toast.duration && this.toast.duration > 0) {
            this.duration.set(this.toast.duration);
            this.startTime.set(Date.now());
            this.currentTime.set(Date.now());
            this.startTimer();
            this.startAnimation();
        }
    }

    private startTimer() {
        this.clearTimer();
        const remaining = this.remainingTime();

        if (remaining > 0) {
            this.timeoutId = setTimeout(() => {
                this.toastService.dismiss(this.toast.id);
            }, remaining);
        }
    }

    private clearTimer() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    private startAnimation() {
        const animate = () => {
            if (!this.isPaused()) {
                this.currentTime.set(Date.now());

                if (this.remainingTime() > 0) {
                    this.animationFrameId = requestAnimationFrame(animate);
                } else {
                    // Auto-dismiss when time runs out
                    this.toastService.dismiss(this.toast.id);
                }
            } else if (this.remainingTime() > 0) {
                // Continue animation loop even when paused to check for resume
                this.animationFrameId = requestAnimationFrame(animate);
            }
        };
        animate();
    }

    private stopAnimation() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    @HostListener('mouseenter')
    onMouseEnter() {
        if (!this.isPaused() && this.duration() > 0) {
            this.clearTimer(); // Stop the timer
            this.pauseStartTime.set(Date.now());
            this.isPaused.set(true);
        }
    }

    @HostListener('mouseleave')
    onMouseLeave() {
        if (this.isPaused() && this.remainingTime() > 0) {
            const pauseDuration = Date.now() - this.pauseStartTime();
            this.totalPausedTime.update((total) => total + pauseDuration);
            this.isPaused.set(false);
            this.startTimer(); // Restart the timer with remaining time
        }
    }

    private cleanup() {
        this.clearTimer();
        this.stopAnimation();
    }

    ngOnDestroy() {
        this.cleanup();
    }
}
